<script lang="ts" setup>
import { Delete, Rank } from "@element-plus/icons-vue";
import { ref } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { LabelledOption } from "~/api";
import { Slotted } from "~/types/Slotted";
import { showError } from "~/utils/handleAPIError";

type LabelledOptionErrors = {
  [key in keyof LabelledOption]: string[];
};

const props = defineProps<{
  readOnly?: boolean;
}>();

const modelValue = defineModel<LabelledOption[]>({ required: true });
const rowIndexReordered = ref<number | undefined>(undefined);

const multipleOptions = ref<string>("");
const addingMultiple = ref(false);

const handleAdd = () => {
  if (!modelValue.value) {
    modelValue.value = [];
  }
  const newLabelledOption: LabelledOption = {
    label: `Option ${modelValue.value.length + 1}`,
    value: `Value${modelValue.value.length + 1}`,
  };

  modelValue.value.push(newLabelledOption);
};

const handleRemove = (val: LabelledOption) => {
  const i = modelValue.value.indexOf(val);
  if (i !== -1) {
    modelValue.value.splice(i, 1);
  }
};

const tableRowClassName = ({
  rowIndex,
}: {
  row: LabelledOption;
  rowIndex: number;
}) => {
  if (rowIndex === rowIndexReordered.value) {
    return "reordered";
  }
  return "";
};

const getErrors = (option: LabelledOption): LabelledOptionErrors => {
  const errors: LabelledOptionErrors = {
    value: [],
    label: [],
  };

  if (option.value.trim().length === 0) {
    errors.value.push("Value is required");
  }

  if (option.label.trim().length === 0) {
    errors.label.push("Label is required");
  }

  // Value must be unique
  if (modelValue.value.filter((o) => o.value === option.value).length > 1) {
    errors.value.push("Values must be unique");
  }

  // Label must be unique
  if (modelValue.value.filter((o) => o.label === option.label).length > 1) {
    errors.label.push("Labels must be unique");
  }

  return errors;
};

const parseLabelValueOptions = () => {
  const lines = multipleOptions.value.split("\n").filter((s) => s.trim());
  if (lines.length === 0) {
    showError("No lines found");
    return;
  }
  const newOptions: LabelledOption[] = [];
  let hasErrors = false;
  for (const line of lines) {
    const [label, valueInit] = // split by tab or comma
      line.split(/[\t,]/).map((s) => s.trim());
    const commaCount = (line.match(/,/g) || []).length;

    let value = valueInit;

    if (label && !value) {
      value = label; // if not value is provided, use label as value
    }

    if (label && value) {
      if (commaCount > 1) {
        showError(`Line contains more than one comma: ${line}`);
        hasErrors = true;
        continue;
      } else {
        newOptions.push({ label: label.trim(), value: value.trim() });
      }
    } else {
      showError("Invalid line: " + line);
      hasErrors = true;
    }
  }

  // ensure we have a list
  if (!modelValue.value) {
    modelValue.value = [];
  }

  // check for duplicates in the combined old and new list
  const testList = [...modelValue.value, ...newOptions];
  const duplicates = testList.filter(
    (o) => testList.filter((x) => x.value === o.value).length > 1,
  );
  if (duplicates.length > 0) {
    // get a distinct list of duplicates
    const distinctDuplicates = duplicates.filter(
      (o, i, a) => a.findIndex((x) => x.value === o.value) === i,
    );
    showError(
      "Duplicate values found: " + distinctDuplicates.map((o) => o.value),
    );
    hasErrors = true;
  }

  if (hasErrors) {
    return;
  }

  modelValue.value.push(...newOptions);

  multipleOptions.value = "";
  addingMultiple.value = false;
};
</script>

<template>
  <div class="LabelledOptionsEditor">
    <div class="add-multiple">
      <div v-if="addingMultiple" class="textareaWithComment">
        <el-input
          v-model="multipleOptions"
          class="textarea"
          :rows="5"
          type="textarea"
          :disabled="props.readOnly"
        />
        <div class="comment">
          Enter lines with "Label, Code" pairs (separated by a comma or a Tab)
          or just labels. Can copy directly from a spreadsheet. For example:
          <code>Option A, CodeA</code>
          <code>Code B</code>
          Click 'Add Options' to add them all.
        </div>
      </div>
      <el-button
        v-if="addingMultiple"
        type="primary"
        class="add-btn"
        :disabled="props.readOnly"
        @click="parseLabelValueOptions"
      >
        Add Options
      </el-button>

      <el-button
        class="cancel-btn"
        :disabled="props.readOnly"
        @click="addingMultiple = !addingMultiple"
      >
        {{ addingMultiple ? "Cancel Adding Multiple" : "Add multiple" }}
      </el-button>
    </div>
    <div class="labelled-options-editor">
      <VueDraggableNext
        class="dragArea"
        :list="modelValue"
        :disabled="props.readOnly"
      >
        <div
          v-for="item in modelValue"
          :key="item.value"
          style="margin-top: 0.7em"
        >
          <span>
            <ElIcon><Rank /></ElIcon>
          </span>
        </div>
      </VueDraggableNext>
      <el-table :data="modelValue" border :row-class-name="tableRowClassName">
        <el-table-column label="Label" prop="label" width="300">
          <template #default="{ row }: Slotted<LabelledOption>">
            <div :class="{ error: getErrors(row).label.length > 0 }">
              <el-input
                v-model="row.label"
                size="small"
                class="label"
                :disabled="props.readOnly"
              />
              <el-text type="danger" size="small">
                {{ getErrors(row).label[0] }}
              </el-text>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Code" prop="value" width="150">
          <template #default="{ row }: Slotted<LabelledOption>">
            <div :class="{ error: getErrors(row).value.length > 0 }">
              <el-input
                v-model="row.value"
                size="small"
                class="value"
                :disabled="props.readOnly"
              />
              <el-text type="danger" size="small">
                {{ getErrors(row).value[0] }}
              </el-text>
            </div>
          </template>
        </el-table-column>

        <el-table-column align="center" width="80">
          <template #default="{ row }: Slotted<LabelledOption>">
            <el-button
              :icon="Delete"
              :disabled="props.readOnly"
              tabindex="-1"
              size="small"
              @click="() => handleRemove(row)"
            />
          </template>
        </el-table-column>
      </el-table>
    </div>
    <el-button class="add-item" :disabled="props.readOnly" @click="handleAdd">
      Add Option
    </el-button>
  </div>
</template>

<style lang="less">
.LabelledOptionsEditor {
  margin-left: -27px; // align with text above

  .add-multiple {
    margin: 0 0 1em 2em;
    .textareaWithComment {
      margin-bottom: 0.5em;
    }
    .add-btn {
      margin-top: 10px;
    }
    .cancel-btn {
      margin-top: 10px;
    }
  }
  .labelled-options-editor {
    display: grid;
    grid-template-columns: 1em auto;
    align-items: center;
    gap: 1em;
    margin: 8px 0;
    margin-left: 30px;
    width: 600px;
    .dragArea {
      margin-top: 2.2em;
      cursor: pointer;
    }

    .order-arrows {
      display: flex;
      flex-direction: column;
      align-items: center;
      .el-button {
        width: min-content;
        margin: 0;
      }
    }
    .error {
      .el-input__wrapper {
        box-shadow: 0 0 0 1px var(--el-color-danger) inset;
      }
    }
    .el-table__row {
      transition: all 500ms;

      &.reordered {
        background-color: var(--el-color-primary-light-9);
        &:hover {
          background-color: var(--el-color-primary-light-9);
        }
      }
    }
  }

  code {
    display: block;
    margin: 4px 0 4px 1em;
  }

  .add-item.el-button {
    margin-bottom: 1em;
    width: min-content;
    margin-left: 30px;
  }

  .textareaWithComment {
    display: flex;
    gap: 1em;
  }
}
</style>
